import React, { useState, useEffect } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import { fetchData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import RefreshIcon from '@mui/icons-material/Refresh';
import FeedIcon from '@mui/icons-material/Feed';
import CopyrightIcon from '@mui/icons-material/Copyright';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import '../shared.css';

function DailySalesSummary() {
    const [dataItem, setDataItem] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [showSearch, setShowSearch] = useState(false);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const { isAuthenticated, companyId, branchId, permissions, token, logout } = useAuth();
    const [totalcgst, setTotalcgst] = useState(0);
    const [totalsgst, setTotalsgst] = useState(0);
    const [totaligst, setTotaligst] = useState(0);
    const [totalnet, setTotalnet] = useState(0);
    const [totalamt, setTotalamt] = useState(0);
    const [totalpurchase, setTotalpurchase] = useState(0);
    const [totalqty, setTotalqty] = useState(0);
    const [totalbills, setTotalbills] = useState(0);

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
        setFromDate(getFormattedStartDate());
        setToDate(getFormattedCurrentDate());
        fetchTableData();
    }, [isAuthenticated, companyId, branchId, token, logout]);

    useEffect(() => {
        let totalcgst = 0, totalsgst = 0, totaligst = 0, totalnet = 0;
        let totalamt = 0, totalpurchase = 0, qty = 0;
        let billNumbers = new Set();
        dataItem.forEach(item => {
            totalcgst += item.CGSTAmount;
            totalsgst += item.SGSTAmount;
            totaligst += item.IGSTAmount;
            totalnet += item.TaxableAmount;
            totalamt += item.TotalAmount;
            totalpurchase += item.PurchaseAmount;
            qty += item.Quantity;
            billNumbers.add(item.BillNumber);
        });
        setTotalcgst(totalcgst);
        setTotalsgst(totalsgst);
        setTotaligst(totaligst);
        setTotalnet(totalnet);
        setTotalamt(totalamt);
        setTotalqty(qty);
        setTotalpurchase(totalpurchase);
        setTotalbills(billNumbers.size);
    }, [dataItem]);

    const getFormattedCurrentDate = () => new Date().toISOString().split('T')[0];
    const getFormattedStartDate = () => {
        const d = new Date(); d.setDate(1); return d.toISOString().split('T')[0];
    };

    const hasReadReportPermission = permissions.includes('Read Reports');
    const [loading, setLoading] = useState(false);

    const fetchTableData = async () => {
        if (hasReadReportPermission) {
            setDataItem([]);
            setLoading(true);
            try {
                const response = await fetchData('ReportApi', `/DailySalesSummary/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
                if (response.responseCode === '1111') {
                    setDataItem(response.response_data);
                }
            } catch (error) {
                console.error('Error fetching Daily Sales Summary Details:', error);
            } finally {
                setLoading(false);
            }
        }
    };

    const filteredDataItem = dataItem.filter((item) =>
        Object.values(item).some((value) =>
            String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
        return new Date(dateString).toLocaleDateString('en-GB', options);
    };

    const handleExportExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Daily Sales Summary');

        const rightAlignColumns = [
            'Quantity', 'MRP', 'MRP On Net', 'Discount %', 'Discount value',
            'Additional Disc%', 'Add Disc Value', 'Cash Discount', 'Balance',
            'GST %', 'CGST Amount', 'SGST Amount', 'IGST Amount', 'Net Value',
            'Gift Voucher', 'Net Value + Tax', 'Purchase Rate', 'Total Purchase Value'
        ];

        worksheet.mergeCells('A1:AM1');
        const headerCaptionCell = worksheet.getCell('A1');
        headerCaptionCell.value = `Daily Sales Summary From : ${formatDate(fromDate)} To : ${formatDate(toDate)}`;
        headerCaptionCell.font = { bold: true, size: 14 };
        headerCaptionCell.alignment = { vertical: 'middle', horizontal: 'left' };

        const headerRowValues = [
            'Date', 'Time', 'Memo No', 'PG', 'IG', 'ISG', 'PF', 'Item No', 'Description', 'HSN Code', 'Quantity',
            'MRP', 'MRP On Net', 'Discount %', 'Discount value', 'Additional Disc%', 'Add Disc Value', 'Cash Discount',
            'Balance', 'GST %', 'CGST Amount', 'SGST Amount', 'IGST Amount', 'Net Value', 'Gift Voucher', 'Net Value + Tax',
            'Mode Of Payment', 'GST Number', 'Sales Man', 'Comments', 'GWP/PWP',
            'Inter-City Stock Transfer', 'Category', 'Group', 'Season Code', 'Size Code'
        ];
        const headerRow = worksheet.addRow(headerRowValues);
        headerRow.eachCell((cell) => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFB6D7A8' } };
            cell.font = { bold: true, color: { argb: 'FF000000' } };
            cell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
            cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        });
        worksheet.columns = headerRowValues.map(() => ({ width: 15 }));

        filteredDataItem.forEach((rowData) => {
            const dataRow = [
                formatDate(rowData.BillDate), rowData.BillTime, rowData.BillNumber,
                rowData.PG, rowData.IG, rowData.ISG, rowData.PF, parseFloat(rowData.Code),
                rowData.ItemName, rowData.HSNCode, rowData.Quantity,
                parseFloat(rowData.Rate.toFixed(2)), parseFloat(rowData.Amount.toFixed(2)),
                rowData.DiscountPercentage, parseFloat(rowData.DiscountAmount.toFixed(2)),
                rowData.AdditionalDiscountPercentage, parseFloat(rowData.AdditionalDiscountAmount.toFixed(2)),
                parseFloat(rowData.CashDiscount.toFixed(2)), parseFloat(rowData.TotalAmount.toFixed(2)),
                rowData.GSTRate, parseFloat(rowData.CGSTAmount.toFixed(2)),
                parseFloat(rowData.SGSTAmount.toFixed(2)), parseFloat(rowData.IGSTAmount.toFixed(2)),
                parseFloat(rowData.TaxableAmount.toFixed(2)), '', parseFloat(rowData.TotalAmount.toFixed(2)),
                rowData.ModeOfPayment, rowData.GSTNumber, rowData.SalesManName, rowData.Remarks,
                rowData.Gwp, rowData.InterStockTransfer, rowData.CategoryName, rowData.ItemGroupName,
                rowData.SeasonCode, rowData.SizeCode
            ];
            const row = worksheet.addRow(dataRow);
            row.eachCell((cell, colNumber) => {
                const headerName = headerRowValues[colNumber - 1];
                if (rightAlignColumns.includes(headerName)) {
                    cell.alignment = { vertical: 'middle', horizontal: 'right' };
                } else {
                    cell.alignment = { vertical: 'middle', horizontal: 'center' };
                }
                cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
            });
        });

        const totalRowValues = [
            '', '', '', '', '', '', '', '', 'Total Bills',
            totalbills, totalqty, '', '', '', '', '', '', '', totalamt, '',
            totalcgst, totalsgst, totaligst, totalnet, '', totalamt,
            '', '', '', '', '', '', '', '', '', ''
        ];
        const totalRow = worksheet.addRow(totalRowValues);
        totalRow.font = { bold: true };
        totalRow.eachCell((cell, colNumber) => {
            const headerName = headerRowValues[colNumber - 1];
            if (rightAlignColumns.includes(headerName)) {
                cell.alignment = { vertical: 'middle', horizontal: 'right' };
            } else {
                cell.alignment = { vertical: 'middle', horizontal: 'center' };
            }
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF2CC' } };
            cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        });

        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), 'DailySalesSummary.xlsx');
    };

    return (
        <div className="pg-container">
            <div className="pg-header">
                <h5 className="pg-title">Daily Sales Summary</h5>
                <div className="pg-header-actions">
                    <div className="pg-date-row">
                        <div className="pg-field">
                            <label className="pg-label" style={{ background: 'var(--pg-bg)' }}>From Date</label>
                            <input className="pg-input" type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} />
                        </div>
                        <div className="pg-field">
                            <label className="pg-label" style={{ background: 'var(--pg-bg)' }}>To Date</label>
                            <input className="pg-input" type="date" value={toDate} onChange={e => setToDate(e.target.value)} />
                        </div>
                        <button className="pg-btn pg-btn-outline" onClick={fetchTableData} disabled={loading}>
                            <RefreshIcon /> Refresh
                        </button>
                    </div>
                    <div className="pg-action-row">
                        <button className="pg-btn pg-btn-success" onClick={handleExportExcel}>
                            <FeedIcon /> Excel
                        </button>
                        <button className="pg-btn pg-btn-outline" onClick={() => setShowSearch(v => !v)}>
                            <SearchIcon /> Search
                        </button>
                    </div>
                    {showSearch && (
                        <input className="pg-input pg-search-input" placeholder="Search…"
                            value={searchQuery} onChange={e => setSearchQuery(e.target.value)} autoFocus />
                    )}
                </div>
            </div>

            {loading && (
                <div className="pg-loading-overlay">
                    <div className="pg-loading-card">
                        <div className="pg-logo-loader">
                            <div className="pg-logo-block pg-block-green"></div>
                            <div className="pg-logo-block pg-block-blue"></div>
                            <div className="pg-logo-block pg-block-red"></div>
                            <div className="pg-logo-block pg-block-yellow"></div>
                        </div>
                        <p className="pg-loading-text">Loading...</p>
                    </div>
                </div>
            )}

            <div className="pg-table-wrap">
                <table className="pg-table">
                    <thead>
                        <tr>
                            <th>Date</th>
                            <th>Memo No</th>
                            <th>PG</th>
                            <th>IG</th>
                            <th>ISG</th>
                            <th>PF</th>
                            <th>Item No</th>
                            <th>Description</th>
                            <th>HSN Code</th>
                            <th style={{ textAlign: 'right' }}>Quantity</th>
                            <th style={{ textAlign: 'right' }}>MRP</th>
                            <th style={{ textAlign: 'right' }}>MRP On Net</th>
                            <th style={{ textAlign: 'right' }}>Discount %</th>
                            <th style={{ textAlign: 'right' }}>Discount value</th>
                            <th style={{ textAlign: 'right' }}>Additional Disc%</th>
                            <th style={{ textAlign: 'right' }}>Add Disc Value</th>
                            <th style={{ textAlign: 'right' }}>Cash Discount</th>
                            <th style={{ textAlign: 'right' }}>Balance</th>
                            <th style={{ textAlign: 'right' }}>GST %</th>
                            <th style={{ textAlign: 'right' }}>CGST Amount</th>
                            <th style={{ textAlign: 'right' }}>SGST Amount</th>
                            <th style={{ textAlign: 'right' }}>IGST Amount</th>
                            <th style={{ textAlign: 'right' }}>Net Value</th>
                            <th>Gift Voucher</th>
                            <th style={{ textAlign: 'right' }}>Net Value + Tax</th>
                            <th>Mode Of Payment</th>
                            <th>GST Number</th>
                            <th>Sales Man</th>
                            <th>Comments</th>
                            <th>GWP/PWP</th>
                            <th>Inter-City Stock Transfer</th>
                            <th>Category</th>
                            <th>Group</th>
                            <th>Season Code</th>
                            <th>Size Code</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredDataItem.length === 0
                            ? <tr><td colSpan={35} style={{ textAlign: 'center', color: '#94a3b8', padding: 16 }}>No records found</td></tr>
                            : filteredDataItem.map((data, index) => (
                                <tr key={index}>
                                    <td>{formatDate(data.BillDate)}</td>
                                    <td>{data.BillNumber}</td>
                                    <td>{data.PG}</td>
                                    <td>{data.IG}</td>
                                    <td>{data.ISG}</td>
                                    <td>{data.PF}</td>
                                    <td>{data.Code}</td>
                                    <td>{data.ItemName}</td>
                                    <td>{data.HSNCode}</td>
                                    <td style={{ textAlign: 'right' }}>{data.Quantity}</td>
                                    <td style={{ textAlign: 'right' }}>{data.Rate ? data.Rate.toFixed(2) : '0.00'}</td>
                                    <td style={{ textAlign: 'right' }}>{data.Amount ? data.Amount.toFixed(2) : '0.00'}</td>
                                    <td style={{ textAlign: 'right' }}>{data.DiscountPercentage}</td>
                                    <td style={{ textAlign: 'right' }}>{data.DiscountAmount ? data.DiscountAmount.toFixed(2) : '0.00'}</td>
                                    <td style={{ textAlign: 'right' }}>{data.AdditionalDiscountPercentage}</td>
                                    <td style={{ textAlign: 'right' }}>{data.AdditionalDiscountAmount ? data.AdditionalDiscountAmount.toFixed(2) : '0.00'}</td>
                                    <td style={{ textAlign: 'right' }}>{data.CashDiscount ? data.CashDiscount.toFixed(2) : '0.00'}</td>
                                    <td style={{ textAlign: 'right' }}>{data.TotalAmount ? data.TotalAmount.toFixed(2) : '0.00'}</td>
                                    <td style={{ textAlign: 'right' }}>{data.GSTRate}</td>
                                    <td style={{ textAlign: 'right' }}>{data.CGSTAmount ? data.CGSTAmount.toFixed(2) : '0.00'}</td>
                                    <td style={{ textAlign: 'right' }}>{data.SGSTAmount ? data.SGSTAmount.toFixed(2) : '0.00'}</td>
                                    <td style={{ textAlign: 'right' }}>{data.IGSTAmount ? data.IGSTAmount.toFixed(2) : '0.00'}</td>
                                    <td style={{ textAlign: 'right' }}>{data.TaxableAmount ? data.TaxableAmount.toFixed(2) : '0.00'}</td>
                                    <td></td>
                                    <td style={{ textAlign: 'right' }}>{data.TotalAmount ? data.TotalAmount.toFixed(2) : '0.00'}</td>
                                    <td>{data.ModeOfPayment}</td>
                                    <td>{data.GSTNumber}</td>
                                    <td>{data.SalesManName}</td>
                                    <td>{data.Remarks}</td>
                                    <td>{data.Gwp}</td>
                                    <td>{data.InterStockTransfer}</td>
                                    <td>{data.CategoryName}</td>
                                    <td>{data.ItemGroupName}</td>
                                    <td>{data.SeasonCode}</td>
                                    <td>{data.SizeCode}</td>
                                </tr>
                            ))
                        }
                        <tr style={{ background: 'linear-gradient(135deg, #1c2a3a 0%, #243447 100%)', color: '#e8f0f8', fontWeight: 700 }}>
                            <td colSpan={8} style={{ textAlign: 'right', padding: '6px 10px' }}>Total Bills: {totalbills}</td>
                            <td style={{ textAlign: 'right', padding: '6px 10px' }}>{totalqty}</td>
                            <td colSpan={8} style={{ padding: '6px 10px' }}></td>
                            <td style={{ textAlign: 'right', padding: '6px 10px' }}>{totalamt.toFixed(2)}</td>
                            <td style={{ padding: '6px 10px' }}></td>
                            <td style={{ textAlign: 'right', padding: '6px 10px' }}>{totalcgst.toFixed(2)}</td>
                            <td style={{ textAlign: 'right', padding: '6px 10px' }}>{totalsgst.toFixed(2)}</td>
                            <td style={{ textAlign: 'right', padding: '6px 10px' }}>{totaligst.toFixed(2)}</td>
                            <td style={{ textAlign: 'right', padding: '6px 10px' }}>{totalnet.toFixed(2)}</td>
                            <td colSpan={12} style={{ padding: '6px 10px' }}></td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div className="pg-watermark"><CopyrightIcon style={{ fontSize: 12 }} /><b>EFFTABS SOLUTIONS LLP</b></div>
        </div>
    );
}

export default DailySalesSummary;
